<template>
	<Modal
		width="large"
		@close="emitCloseModal"
		:texts="{ headerPrimary: modalTitle }"
		:showCancelBtn="false"
		:showConfirmBtn="false"
	>
		<div class="modern-form">
			<!-- Wizard Stepper -->
			<div class="wizard-stepper">
				<div class="wizard-steps-row">
					<div
						v-for="step in totalSteps"
						:key="`task-step-${step}`"
						class="wizard-step"
						:class="{ active: currentStep === step, completed: currentStep > step }"
						@click="onStepClick(step)"
					>
						<span class="wizard-step-index">
							<span v-if="currentStep > step">✓</span>
							<span v-else>{{ step }}</span>
						</span>
						<span class="wizard-step-label">{{ getStepTitle(step) }}</span>
					</div>
				</div>
				<div class="wizard-progress">
					<div class="wizard-progress-bar" :style="{ width: progressPercent }"></div>
				</div>
			</div>

			<!-- Step 1: Task Details -->
			<div class="form-section" v-if="currentStep === 1">
				<h3 class="section-title">{{ getStepTitle(1) }}</h3>
				<div class="form-grid">
					<!-- Task languages: which languages will this task be in? -->
					<div class="form-field full-width task-languages-field">
						<label class="field-label">{{ $t('task_languages') }}</label>
						<p class="field-hint">{{ $t('task_languages_hint') }}</p>
						<div class="language-checkboxes">
							<label
								v-for="(lang, idx) in languages"
								:key="'lang-' + idx"
								class="language-checkbox-option"
							>
								<input type="checkbox" :value="lang.label" v-model="selectedTaskLanguages" />
								<img v-if="lang.logo_url" class="lang-flag" :src="lang.logo_url" alt="" />
								<span>{{ $t(lang.label) }}</span>
							</label>
						</div>
						<span v-if="isErrorShown('task_languages')" class="error-msg">{{ $t('please_enter') }} {{ $t('task_languages') }}</span>
					</div>
					<!-- Title: one input per selected language -->
					<template v-for="langLabel in selectedTaskLanguages">
						<div class="form-field titles-container" :key="'title-' + langLabel">
							<label class="field-label">
								<img
									v-if="getLangFlag(langLabel)"
									class="field-label-icon"
									:src="getLangFlag(langLabel)"
									alt=""
								/>
								{{ $t('title') }} ({{ $t(langLabel) }})
							</label>
							<input
								class="modern-input"
								dir="auto"
								@keypress="(e) => isCharValid(e, langLabel)"
								@click.right.prevent
								:class="{ 'flash-field': isErrorShown('title') }"
								:placeholder="$t('insert_text')"
								type="text"
								v-model.trim="task.titles[langLabel]"
							/>
						</div>
					</template>
					<div v-if="selectedTaskLanguages.length === 0" class="form-field full-width">
						<p class="field-hint select-languages-first">{{ $t('select') }} {{ $t('task_languages') }} {{ $t('to_continue') || 'to continue' }}</p>
					</div>
					<div class="form-field full-width">
						<label class="field-label">{{ $t('description') }}</label>
						<textarea
							class="modern-input modern-input-textarea"
							dir="auto"
							rows="3"
							:placeholder="$t('insert_text')"
							v-model.trim="task.description"
						/>
					</div>
					<div class="form-field">
						<label class="field-label">{{ $t('task_type') }}</label>
						<Select
							class="modern-select"
							:class="{ 'flash-field': isErrorShown('task_type') }"
							:placeholder="$t('select')"
							v-model="task.type"
						>
							<Option v-for="(type, idx) in taskTypes" :key="type + idx" :label="$t(type)" :value="type" />
						</Select>
					</div>
					<div class="form-field">
						<label class="field-label">{{ $t('task_category') }}</label>
						<Select
							class="modern-select"
							:class="{ 'flash-field': isErrorShown('task_category') }"
							:placeholder="$t('select')"
							v-model="task.category"
							:sideLabelStyle="sideLabelStyle"
							:disabled="!task.type"
						>
							<Option
								v-for="(category, idx) in taskCategories"
								:key="category + idx"
								:label="$t(category.name)"
								:value="category.name"
								:isSideLabel="true"
								:sideLabelStyle="getSideLabelStyle(category.color)"
							>
							</Option>
						</Select>
					</div>
				</div>
			</div>

			<!-- Step 2: Schedule -->
			<div class="form-section" v-if="currentStep === 2">
				<h3 class="section-title">{{ getStepTitle(2) }}</h3>
				<div class="form-grid">
					<div class="form-field">
						<label class="field-label">{{ $t('schedule') }}</label>
						<Select
							class="modern-select"
							:class="{ 'flash-field': isErrorShown('schedule') }"
							v-model="task.schedule"
							:placeholder="$t('select')"
							:disabled="!task.category"
						>
							<Option
								v-for="(schedule, idx) in schedules"
								:key="schedule.name + idx"
								:label="$t(schedule.name)"
								:value="schedule.name"
							>
							</Option>
						</Select>
					</div>
					<div class="form-field" v-if="isTaskTypeActive">
						<label class="field-label">{{ $t('schedule_dates') }}</label>
						<Select
							class="modern-select"
							:class="{ 'flash-field': isErrorShown('schedule_dates') }"
							@input="handleSelectedScheduleDates"
							:placeholder="$t('select')"
							:multiple="isMultipleScheduleSelectors"
							:disabled="!task.scheduleDates[0] && scheduleOptions.length === 0"
							:value="task.scheduleDates"
						>
							<Option v-for="(date, idx) in scheduleOptions" :key="date + idx" :label="$t(date)" :value="date">
							</Option>
						</Select>
					</div>
					<div class="form-field" v-else>
						<label class="field-label">{{ $t('send_date') }}</label>
						<v-menu v-model="menuMP" max-width="290px" min-width="290px" offset-y transition="scale-transition">
							<template v-slot:activator="{ on }">
								<v-text-field
									:value="formattedTaskSendDate"
									:label="`${$t('date')}`"
									prepend-inner-icon="mdi-calendar"
									readonly
									dense
									hide-details
									class="modern-v-input"
									:class="{ 'flash-field': isErrorShown('send_date') }"
									v-on="on"
								></v-text-field>
							</template>
							<v-date-picker
								no-title
								show-adjacent-months
								v-model="task.send_date"
								@input="menuMP = false"
								:show-current="formattedTaskSendDate || true"
							></v-date-picker>
						</v-menu>
					</div>
					<div class="form-field">
						<label class="field-label">{{ $t('hour') }}</label>
						<Select
							class="modern-select"
							:class="{ 'flash-field': isErrorShown('hour') || isErrorShown('hour_past') }"
							:placeholder="$t('select')"
							v-model="task.hour"
							:disabled="!task.send_date && task.scheduleDates.length === 0"
						>
							<Option v-for="(hour, idx) in hours" :key="hour + idx" :label="hour" :value="hour"> </Option>
						</Select>
					</div>
					<div class="form-field">
						<label class="field-label">{{ $t('duration') }}</label>
						<Select
							class="modern-select"
							:placeholder="$t('select')"
							:value="selectedDuration"
							@input="handleSelectedDuration"
						>
							<Option :label="$t('shift')" :value="''"></Option>
							<Option :label="$t('1 day')" :value="'all day'"></Option>
							<Option :label="$t('1 week')" :value="'all week'"></Option>
							<Option :label="$t('1 month')" :value="'all month'"></Option>
						</Select>
					</div>
					<div class="form-field">
						<label class="field-label">{{ $t('auto_start') }}</label>
						<Select class="modern-select" :placeholder="$t('select')" v-model="task.is_auto_start">
							<Option :label="$t('yes')" :value="'true'"></Option>
							<Option :label="$t('no')" :value="'false'"></Option>
						</Select>
					</div>
					<div class="form-field" v-if="isTypeSequential">
						<label class="field-label">{{ $t('buffer_time') }}</label>
						<Select class="modern-select" :placeholder="$t('select')" v-model="task.buffer_time">
							<Option v-for="(minute, idx) in bufferTimeMins" :key="minute + idx" :label="minute" :value="minute">
							</Option>
						</Select>
					</div>
				</div>
			</div>

			<!-- Step 3: Assignment & Target -->
			<div class="form-section" v-if="currentStep === 3">
				<h3 class="section-title">{{ getStepTitle(3) }}</h3>
				<div class="form-grid">
					<div class="form-field full-width">
						<label class="field-label">{{ $t('task_assignment') }}</label>
						<div class="radio-group">
							<label class="radio-option">
								<input type="radio" value="room" v-model="task.task_type" />
								<span>{{ $t('task_assignment_room') }}</span>
							</label>
							<label class="radio-option">
								<input type="radio" value="personal" v-model="task.task_type" />
								<span>{{ $t('task_assignment_personal') }}</span>
							</label>
						</div>
					</div>
					<div class="form-field" v-if="task.task_type === 'personal'">
						<label class="field-label">{{ $t('assignee') }}</label>
						<Select
							class="modern-select"
							:class="{ 'flash-field': isErrorShown('assignee') }"
							:placeholder="$t('select')"
							v-model="task.assignee_id"
						>
							<Option
								v-for="(c, idx) in contacts"
								:key="c.id + idx"
								:label="assigneeLabel(c)"
								:value="c.id"
							/>
						</Select>
					</div>
					<div class="form-field full-width" v-if="task.task_type !== 'personal'">
						<label class="field-label">{{ $t('task_target_label') }}</label>
						<div class="radio-group">
							<label class="radio-option">
								<input type="radio" value="working_areas" v-model="taskTargetBy" @change="onTargetByChange('working_areas')" />
								<span>{{ $t('task_target_by_working_areas') }}</span>
							</label>
							<label class="radio-option">
								<input type="radio" value="rooms" v-model="taskTargetBy" @change="onTargetByChange('rooms')" />
								<span>{{ $t('task_target_by_rooms') }}</span>
							</label>
						</div>
					</div>
					<div class="form-field" v-show="task.task_type !== 'personal' && taskTargetBy === 'working_areas'">
						<label class="field-label">{{ $t('working_areas') }}</label>
						<Select
							class="modern-select"
							:class="{ 'flash-field': isErrorShown('working_areas') }"
							v-model="task.selectedWorkingAreaIds"
							:multiple="true"
						>
							<Option
								v-for="(workingArea, idx) in workingAreas"
								:key="workingArea.id + idx"
								:label="workingArea.area_name"
								:value="workingArea.id"
							/>
						</Select>
					</div>
					<div class="form-field" v-show="task.task_type !== 'personal' && taskTargetBy === 'rooms'">
						<label class="field-label">{{ $t('rooms') }}</label>
						<Select
							class="modern-select"
							:class="{ 'flash-field': isErrorShown('rooms') }"
							@input="handleSelectedRooms"
							:placeholder="$t('select')"
							:multiple="true"
							:value="selectedTaskRoomsIds"
						>
							<Option
								v-for="(room, idx) in siteRooms"
								:key="room + idx"
								:label="room.display_name"
								:value="room.id"
							>
							</Option>
						</Select>
					</div>
					<div class="form-field">
						<label class="field-label">{{ $t('sla') }}</label>
						<v-text-field v-model="task.sla" :rules="slaRules" :counter="3" dense hide-details class="modern-v-input sla-input"></v-text-field>
					</div>
				</div>
			</div>
		</div>

		<!-- Wizard Footer -->
		<template v-slot:footer>
			<div class="wizard-footer">
				<button type="button" class="modern-button secondary-button" @click="emitCloseModal">
					{{ $t('cancel') }}
				</button>
				<div class="wizard-footer-right">
					<button
						v-if="currentStep > 1"
						type="button"
						class="modern-button secondary-button"
						@click="prevStep"
					>
						{{ $t('back') || 'Back' }}
					</button>
					<button
						type="button"
						class="modern-button primary-button"
						@click="onPrimaryClick"
						:disabled="isConfirmDisabled"
					>
						{{ primaryStepLabel }}
					</button>
				</div>
			</div>
		</template>
	</Modal>
</template>

<script>
import Modal from '@/modules/common/components/Modal'
import Select from '@/modules/common/components/Select'
import Option from '@/modules/common/components/Option'
import { localeData } from '@/modules/common/services/localeService'
import utilService from '@/modules/common/services/utilService'
import {
	taskMultiLangsInputFields,
	taskInputs,
	taskTypes,
	taskSchedules,
	taskOtherSchedules,
	hours,
	bufferTimeMins
} from '@/modules/task/consts/consts.js'

export default {
	name: 'TaskModal',
	components: { Modal, Select, Option },
	props: {
		taskProp: { type: Object, required: true },
		languages: { type: Array, required: true },
		taskCategories: { type: Array, required: true },
		rooms: { type: Array, required: true },
		workingAreas: { type: Array, required: true },
		contacts: { type: Array, default: () => [] }
	},
	created() {
		if (!this.task.titles || typeof this.task.titles !== 'object') {
			this.task.titles = {}
		}
		if (this.task.description == null) {
			this.task.description = ''
		}
		this.addLangsToTaskInputs()
		this.setDefaultForm()
		this.initSelectedTaskLanguages()
		if (this.task.task_type !== 'personal') {
			this.selectedRooms = Array.isArray(this.task.rooms) ? this.task.rooms.map((r) => ({ ...r })) : []
			this.taskTargetBy = this.task.rooms && this.task.rooms.length > 0 ? 'rooms' : 'working_areas'
			if (this.taskTargetBy === 'working_areas' && this.workingAreas && this.workingAreas.length === 1) {
				this.task.selectedWorkingAreaIds = [this.workingAreas[0].id]
			}
		}
	},
		data() {
		return {
			currentStep: 1,
			totalSteps: 3,
			taskTargetBy: 'working_areas',
			taskMultiLangsInputFields,
			taskInputs,
			taskTypes,
			taskSchedules,
			taskOtherSchedules,
			hours,
			bufferTimeMins,
			scheduleOptions: [],
			selectedLanguage: '',
			selectedTaskLanguages: [],
			task: JSON.parse(JSON.stringify(this.taskProp)),
			siteRooms: JSON.parse(JSON.stringify(this.rooms)),
			selectedRooms: [],
			errors: [],
			menuMP: false,
			slaRules: [
				(v) => !!v || this.$t('please_enter') + ' ' + this.$t('sla'),
				(v) => (v && /^\d+$/.test(v)) || this.$t('sla') + ' ' + this.$t('must_be_numbers'),
				(v) => (v && v.length <= 3) || this.$t('sla') + ' ' + this.$t('must_be_3_digits')
			],
			isConfirmDisabled: false,
			selectedDuration: ''
		}
	},
	methods: {
		emitCloseModal() {
			this.$emit('closeModal')
		},
		// ── Step navigation ──────────────────────────────────────────────
		nextStep() {
			this.errors = []
			if (this.currentStep === 1 && !this.checkStep1()) return
			if (this.currentStep === 2 && !this.checkStep2()) return
			if (this.currentStep < this.totalSteps) this.currentStep++
		},
		prevStep() {
			if (this.currentStep > 1) this.currentStep--
		},
		onPrimaryClick() {
			if (this.currentStep < this.totalSteps) {
				this.nextStep()
			} else {
				this.saveTask()
			}
		},
		onStepClick(step) {
			if (step < this.currentStep) this.currentStep = step
		},
		getStepTitle(step) {
			if (step === 1) return this.$t('task_details') || 'Task details'
			if (step === 2) return this.$t('schedule') || 'Schedule'
			return this.$t('task_assignment') || 'Assignment'
		},
		// ── Per-step validation ──────────────────────────────────────────
		checkStep1() {
			const { titles, type, category } = this.task
			this.errors = []
			if (!this.selectedTaskLanguages || this.selectedTaskLanguages.length === 0) {
				this.errors.push('task_languages')
			}
			const allSelectedTitlesFilled =
				this.selectedTaskLanguages &&
				this.selectedTaskLanguages.length > 0 &&
				this.selectedTaskLanguages.every((lang) => titles[lang] && String(titles[lang]).trim())
			if (!allSelectedTitlesFilled) this.errors.push('title')
			if (!type) this.errors.push('task_type')
			if (!category) this.errors.push('task_category')
			return this.errors.length === 0
		},
		checkStep2() {
			const { schedule, scheduleDates, send_date, hour } = this.task
			this.errors = []
			if (!schedule) this.errors.push('schedule')
			if (this.isTaskTypeActive) {
				if (scheduleDates.length === 0) this.errors.push('schedule_dates')
			} else {
				if (!send_date) this.errors.push('send_date')
				const date = this.getDateWithNewHour(send_date, hour)
				if (send_date && date && utilService.isPastDate(date)) this.errors.push('hour_past')
			}
			if (!hour) this.errors.push('hour')
			return this.errors.length === 0
		},
		// ── Final form validation & save ─────────────────────────────────
		checkForm() {
			const { sla } = this.task
			this.errors = []
			if (sla) {
				if (!/^\d+$/.test(sla) || sla.length > 3) this.errors.push('sla')
			}
			if (this.task.task_type === 'personal') {
				if (!this.task.assignee_id) this.errors.push('assignee')
			} else if (this.taskTargetBy === 'working_areas') {
				if (!this.task.selectedWorkingAreaIds || this.task.selectedWorkingAreaIds.length === 0) {
					this.errors.push('working_areas')
				}
			} else {
				if (!this.selectedRooms || this.selectedRooms.length === 0) this.errors.push('rooms')
			}
		},
		saveTask() {
			if (this.task.task_type === 'personal') {
				this.task.rooms = []
				this.task.selectedWorkingAreaIds = []
			} else if (this.taskTargetBy === 'working_areas') {
				this.task.rooms = []
				this.task.selectedWorkingAreaIds = this.task.selectedWorkingAreaIds || []
				this.task.task_type = 'room'
				this.task.assignee_id = null
			} else {
				this.task.rooms = this.selectedRooms
				this.task.selectedWorkingAreaIds = []
				this.task.task_type = 'room'
				this.task.assignee_id = null
			}
			this.checkForm()
			if (this.errors.length) return
			this.isConfirmDisabled = true
			this.$emit('save', this.task)
		},
		// ── Form helpers ─────────────────────────────────────────────────
		setDefaultForm() {
			const currWebLang = localeData.language
			const { task, languages } = this
			if (languages.length > 0) {
				this.selectedLanguage = this.languages.find((lang) => lang.value === currWebLang).label
			}
			if (task.schedule) {
				this.handleSchedule(task.schedule)
			}
			this.setSelectedDuration()
		},
		setSelectedDuration() {
			const { task } = this
			let selectedDuration = ''
			if (task.duration.is_all_day) selectedDuration = 'all day'
			if (task.duration.is_all_week) selectedDuration = 'all week'
			if (task.duration.is_all_month) selectedDuration = 'all month'
			this.selectedDuration = selectedDuration
		},
		handleSelectedDuration(value) {
			this.task.duration.is_all_day = value === 'all day'
			this.task.duration.is_all_week = value === 'all week'
			this.task.duration.is_all_month = value === 'all month'
			this.selectedDuration = value
		},
		getSideLabelStyle(color) {
			return `border-left: 10px solid ${color}; float:left; width: initial;`
		},
		handleSchedule(value) {
			if (!value) return
			const schedules = this.getSchedules()
			const schedule = schedules.find((schedule) => schedule.name === value)
			if (!schedule) return
			if (schedule.options.length) {
				this.scheduleOptions = schedule.options
			}
		},
		getSchedules() {
			const { taskSchedules, taskOtherSchedules } = this
			return this.isTaskTypeActive ? taskSchedules : taskOtherSchedules
		},
		addLangsToTaskInputs() {
			const { languages } = this
			if (languages.length) {
				languages.forEach((lang) => {
					const { label } = lang
					this.$set(this.task.titles, label, this.task.titles[label] || '')
				})
			}
		},
		isCharValid(event, languageLabel) {
			const lang = languageLabel != null ? languageLabel : this.selectedLanguage
			const { key } = event
			const isValid = utilService.isCharValid(lang, key)
			if (!isValid) {
				this.addError('invalid_language')
				event.preventDefault()
			} else {
				this.removeError('invalid_language')
			}
		},
		getLangFlag(langLabel) {
			const lang = this.languages.find((l) => l.label === langLabel)
			return lang ? lang.logo_url || '' : ''
		},
		initSelectedTaskLanguages() {
			const { titles } = this.task
			if (!titles || typeof titles !== 'object') return
			const withTitle = Object.keys(titles).filter((lang) => titles[lang] && String(titles[lang]).trim())
			if (withTitle.length > 0) {
				this.selectedTaskLanguages = withTitle
			} else if (this.languages.length > 0) {
				this.selectedTaskLanguages = [this.selectedLanguage].filter(Boolean)
				if (this.selectedTaskLanguages.length === 0) this.selectedTaskLanguages = [this.languages[0].label]
			}
		},
		assigneeLabel(c) {
			return c.username ? `${c.username} (${c.phone || ''})` : c.phone || `#${c.id}`
		},
		onTargetByChange(mode) {
			if (mode === 'working_areas') {
				this.selectedRooms = []
			} else {
				this.task.selectedWorkingAreaIds = []
			}
		},
		handleSelectedScheduleDates(selectedDates) {
			if (!selectedDates) return
			this.task.scheduleDates = Array.isArray(selectedDates) ? selectedDates : [selectedDates]
		},
		handleSelectedRooms(roomsIds) {
			if (!roomsIds || !Array.isArray(roomsIds)) return
			this.selectedRooms = this.siteRooms.filter((room) => roomsIds.includes(room.id))
		},
		addError(error) {
			this.errors.push(error)
		},
		removeError(error) {
			if (this.errors.includes(error)) {
				this.errors = this.errors.filter((err) => err !== error)
			}
		},
		isErrorShown(error) {
			return this.errors.includes(error)
		},
		getDateWithNewHour(date, hour) {
			if (!date || !hour) return null
			const [h, m] = hour.split(':').map(Number)
			const dateObj =
				typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)
					? (() => {
							const [y, mo, d] = date.split('-').map(Number)
							return new Date(y, mo - 1, d)
					  })()
					: new Date(date)
			dateObj.setHours(h, m, 0, 0)
			return dateObj
		}
	},
	computed: {
		modalTitle() {
			return this.task.id ? this.$t('edit_task') : this.$t('new_task')
		},
		primaryStepLabel() {
			return this.currentStep < this.totalSteps ? this.$t('next') || 'Next' : this.$t('OK')
		},
		progressPercent() {
			return `${(this.currentStep / this.totalSteps) * 100}%`
		},
		sideLabelStyle() {
			const { category } = this.task
			if (!category) return ''
			const categoryObj = this.taskCategories.find((c) => c.name === category)
			if (!categoryObj || !categoryObj.color) return ''
			return this.getSideLabelStyle(categoryObj.color)
		},
		selectedLangFlag() {
			const { languages } = this
			return languages.find((lang) => lang.label === this.selectedLanguage).logo_url || ''
		},
		isMultipleScheduleSelectors() {
			return this.isTaskTypeActive
		},
		schedules() {
			return this.isTaskTypeActive ? this.taskSchedules : this.taskOtherSchedules
		},
		taskType() {
			return this.task.type
		},
		formattedTaskSendDate() {
			const { send_date } = this.task
			if (!send_date) return ''
			try {
				const dateObj = new Date(send_date)
				if (isNaN(dateObj.getTime())) return ''
				const year = dateObj.getFullYear()
				const month = (dateObj.getMonth() + 1).toString().padStart(2, '0')
				const day = dateObj.getDate().toString().padStart(2, '0')
				return `${year}-${month}-${day}`
			} catch {
				return ''
			}
		},
		selectedTaskRoomsIds() {
			const { rooms } = this.task
			if (!rooms || rooms.length === 0) return []
			return rooms.map((room) => room.id)
		},
		isTypeSequential() {
			return this.taskType === 'sequential'
		},
		isTypeSchedule() {
			return this.taskType === 'schedule'
		},
		isTaskTypeActive() {
			return this.isTypeSchedule || this.isTypeSequential
		}
	},
	watch: {
		taskProp() {
			this.currentStep = 1
		},
		'task.type': function () {
			this.task.schedule = ''
			this.task.buffer_time = 15
		},
		'task.schedule': function (newValue) {
			if (this.scheduleOptions.length > 0) {
				this.scheduleOptions = []
			}
			this.task.scheduleDates = []
			this.task.send_date = null
			this.handleSchedule(newValue)
		},
		'task.scheduleDates': function () {
			if (this.isTaskTypeActive && this.task.scheduleDates.length === 0) {
				this.task.hour = ''
			}
		}
	}
}
</script>

<style lang="scss" scoped>
@use '@/styles/mixins' as *;
@use '@/styles/vars' as *;

// Match Working Area modal palette and layout
$primary: #007bff;
$primary-hover: #0056b3;
$background: #ffffff;
$surface: #f8f9fa;
$border: #e0e0e0;
$border-light: #f0f0f0;
$text-primary: #212529;
$text-secondary: #6c757d;
$shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.05);
$radius: 8px;
$radius-sm: 6px;

.modern-form {
	display: flex;
	flex-direction: column;
	gap: 24px;
	padding: 0 24px 24px;
}

.wizard-stepper {
	display: flex;
	flex-direction: column;
	align-items: stretch;
	gap: 8px;
	margin-bottom: 0;
}

.wizard-steps-row {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 16px;
}

.wizard-step {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	cursor: pointer;
	transition: all 0.2s ease;
	margin-right: 16px;
	&.active {
		.wizard-step-index {
			border-color: $primary;
			background-color: rgba(0, 123, 255, 0.08);
			color: $primary;
		}
		.wizard-step-label {
			color: $primary;
			font-weight: 600;
		}
	}
	&.completed {
		.wizard-step-index {
			border-color: $primary;
			background-color: $primary;
			color: #fff;
		}
	}
	&:hover .wizard-step-index {
		border-color: $primary;
		transform: translateY(-1px);
	}
}

.wizard-step-index {
	width: 32px;
	height: 32px;
	border-radius: 50%;
	border: 2px solid $border;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 14px;
	color: $text-secondary;
	background-color: $background;
	box-shadow: 0 2px 4px rgba(15, 23, 42, 0.12);
}

.wizard-step-label {
	font-size: 14px;
	color: $text-secondary;
	font-weight: 500;
}

.wizard-progress {
	position: relative;
	width: 100%;
	height: 3px;
	border-radius: 999px;
	background-color: $border-light;
	overflow: hidden;
}

.wizard-progress-bar {
	position: absolute;
	top: 0;
	left: 0;
	height: 100%;
	border-radius: inherit;
	background: linear-gradient(90deg, $primary, lighten($primary, 10%));
	transition: width 0.25s ease;
}

.form-section {
	background: $background;
	border-radius: $radius;
	padding: 20px;
	box-shadow: $shadow-sm;
	border: 1px solid $border-light;
}

.section-title {
	font-size: 16px;
	font-weight: 600;
	color: $text-primary;
	margin: 0 0 20px 0;
	padding-bottom: 12px;
	border-bottom: 2px solid $border-light;
	letter-spacing: -0.01em;
}

.form-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
	gap: 20px;
}

.form-field {
	display: flex;
	flex-direction: column;
	gap: 8px;

	&.full-width {
		grid-column: 1 / -1;
	}
}

.field-label {
	display: flex;
	align-items: center;
	gap: 0.35rem;
	font-size: 13px;
	font-weight: 500;
	color: $text-primary;
	margin: 0;
	letter-spacing: 0.01em;
}

.field-label-icon {
	width: 18px;
	height: 18px;
	object-fit: contain;
	@include rtl {
		order: 1;
	}
}

.field-hint {
	font-size: 12px;
	color: $text-secondary;
	margin: 0 0 10px 0;
	line-height: 1.4;
}

.task-languages-field .error-msg {
	margin-top: 6px;
}

.language-checkboxes {
	display: flex;
	flex-wrap: wrap;
	gap: 12px 20px;
}

.language-checkbox-option {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	cursor: pointer;
	font-size: 14px;
	color: $text-primary;
	user-select: none;
	input[type='checkbox'] {
		width: 1rem;
		height: 1rem;
		accent-color: $primary;
		cursor: pointer;
	}
	.lang-flag {
		width: 20px;
		height: 20px;
		object-fit: contain;
	}
}

.select-languages-first {
	font-style: italic;
	color: $text-secondary;
}

.modern-input {
	width: 100%;
	padding: 10px 14px;
	font-size: 14px;
	border: 1.5px solid $border;
	border-radius: $radius-sm;
	background: $background;
	color: $text-primary;
	transition: all 0.2s ease;

	&:focus {
		outline: none;
		border-color: $primary;
		box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
	}

	&::placeholder {
		color: $text-secondary;
	}

	&.modern-input-textarea {
		resize: vertical;
		min-height: 72px;
	}

	&.flash-field {
		border-color: #dc3545 !important;
		animation: flashBorder 1s ease-in-out 1;
		background-color: rgba(220, 53, 69, 0.08);
	}
}

.modern-select {
	width: 100% !important;
	height: auto !important;
	min-height: 42px;

	::v-deep .md-field,
	::v-deep .md-select {
		min-height: 42px;
	}
	::v-deep .md-input,
	::v-deep .md-select-value {
		padding: 10px 14px !important;
		font-size: 14px !important;
		border: 1.5px solid $border !important;
		border-radius: $radius-sm !important;
		background-color: $background !important;
		transition: all 0.2s ease !important;
		min-height: 42px !important;
	}
	&:focus-within ::v-deep .md-input,
	&:focus-within ::v-deep .md-select-value {
		border-color: $primary !important;
		box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1) !important;
	}

	&.flash-field ::v-deep .md-input,
	&.flash-field ::v-deep .md-select-value {
		border-color: #dc3545 !important;
		animation: flashBorder 1s ease-in-out 1;
		background-color: rgba(220, 53, 69, 0.08);
	}
}

.modern-v-input {
	::v-deep .v-input__slot,
	::v-deep .v-input__control {
		min-height: 42px !important;
	}
	::v-deep .v-text-field__slot input {
		padding: 10px 14px;
		font-size: 14px;
	}
	::v-deep .v-input__slot::before {
		border-color: $border;
	}
	&.sla-input {
		max-width: 100px;
	}

	&.flash-field ::v-deep .v-input__slot::before {
		border-color: #dc3545 !important;
	}
	&.flash-field ::v-deep .v-input__slot input {
		background-color: rgba(220, 53, 69, 0.08);
	}
}

@keyframes flashBorder {
	0%, 100% { border-color: #dc3545; }
	25%, 75% { border-color: transparent; }
	50% { border-color: #dc3545; }
}

.radio-group {
	display: flex;
	flex-wrap: wrap;
	gap: 0.75rem 1.25rem;
}

.radio-option {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	cursor: pointer;
	font-size: 14px;
	color: $text-primary;
	user-select: none;
	input[type='radio'] {
		width: 1rem;
		height: 1rem;
		accent-color: $primary;
		cursor: pointer;
	}
	span {
		font-weight: 400;
	}
	&--disabled {
		cursor: not-allowed;
		opacity: 0.7;
		input[type='radio'] {
			cursor: not-allowed;
		}
	}
}

.radio-option-hint {
	font-size: 12px;
	color: $text-secondary;
	font-weight: 400;
}

.wizard-footer {
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	padding: 16px 24px;
	box-sizing: border-box;
}

.wizard-footer-right {
	display: flex;
	gap: 8px;
}

.modern-button {
	padding: 10px 20px;
	border-radius: $radius-sm;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	border: none;
	transition: background-color 0.15s ease, opacity 0.15s ease;
	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
}

.primary-button {
	background-color: $primary;
	color: #fff;
	&:hover:not(:disabled) {
		background-color: $primary-hover;
	}
}

.secondary-button {
	background-color: transparent;
	color: $text-secondary;
	border: 1.5px solid $border;
	&:hover:not(:disabled) {
		background-color: $surface;
	}
}

.error-msg {
	@include error-msg;
}

.md-menu {
	z-index: 99;
}
</style>
